import type {
  CatalogIngredient,
  CatalogRecipe,
  IngredientDraft,
  RecipeComponent,
  RecipeDraft,
} from "./types";

/** Names match without case and diacritics, as in the backend (catalog.model.normalize_name). */
export const normalizeName = (name: string) =>
  name.trim().toLowerCase().normalize("NFD").replace(/\p{Mn}/gu, "");

/** id from a name, as the backend makes them: "Kuřecí maso" -> "kureci-maso" */
export const slug = (name: string) => normalizeName(name).replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

/** the ids the backend accepts (catalog.model) */
export const INGREDIENT_ID = /^[a-z0-9]+(-[a-z0-9]+)*$/;
export const RECORD_ID = /^[A-Za-z0-9][A-Za-z0-9_-]*$/;

/** every detector class some ingredient uses, sorted */
export const knownDetectorClasses = (ingredients: CatalogIngredient[]) =>
  [...new Set(ingredients.flatMap((i) => i.detector_classes))].sort((a, b) => a.localeCompare(b));

/** ingredient id -> how many current recipes use it */
export function recipeUsage(recipes: CatalogRecipe[]): Map<string, number> {
  const usage = new Map<string, number>();
  for (const recipe of recipes) {
    for (const component of recipe.components) usage.set(component.ingredient_id, (usage.get(component.ingredient_id) ?? 0) + 1);
  }
  return usage;
}

const sameComponents = (a: RecipeComponent[], b: RecipeComponent[]) =>
  a.length === b.length &&
  a.every((c, i) => c.ingredient_id === b[i].ingredient_id && !!c.after_baking === !!b[i].after_baking);

export const sameStrings = (a: string[], b: string[]) => a.length === b.length && a.every((value, i) => value === b[i]);

export const sameRecipe = (a: RecipeDraft, b: RecipeDraft) =>
  a.name === b.name && sameComponents(a.components, b.components) && sameStrings(a.extra_ignore, b.extra_ignore);

export const sameIngredient = (a: IngredientDraft, b: IngredientDraft) =>
  a.name === b.name &&
  sameStrings(a.aliases, b.aliases) &&
  sameStrings(a.detector_classes, b.detector_classes) &&
  (a.extra_min_count ?? null) === (b.extra_min_count ?? null) &&
  (a.extra_min_share ?? null) === (b.extra_min_share ?? null);

/** sauces are segmented: their evidence is a share of the pizza, not a count of pieces */
export const isSauceClass = (detectorClass: string) => detectorClass.endsWith("_sauce");

export interface ComponentDiff {
  added: string[];
  removed: string[];
  /** the "after baking" mark changed */
  moved: string[];
}

export function componentDiff(before: RecipeComponent[], after: RecipeComponent[]): ComponentDiff {
  const old = new Map(before.map((c) => [c.ingredient_id, !!c.after_baking]));
  const next = new Map(after.map((c) => [c.ingredient_id, !!c.after_baking]));
  return {
    added: after.filter((c) => !old.has(c.ingredient_id)).map((c) => c.ingredient_id),
    removed: before.filter((c) => !next.has(c.ingredient_id)).map((c) => c.ingredient_id),
    moved: after.filter((c) => old.has(c.ingredient_id) && old.get(c.ingredient_id) !== !!c.after_baking).map((c) => c.ingredient_id),
  };
}

/** trimmed names without duplicates (two names are one when they differ only in case or diacritics) */
export function uniqueNames(names: string[]): string[] {
  const seen = new Set<string>();
  return names
    .map((name) => name.trim())
    .filter((name) => {
      const key = normalizeName(name);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

/** what is saved: trimmed, no duplicates, the name among the aliases (first when it was missing) */
export function cleanIngredient(draft: IngredientDraft): IngredientDraft {
  const name = draft.name.trim();
  const aliases = uniqueNames(draft.aliases);
  if (name && !aliases.some((alias) => normalizeName(alias) === normalizeName(name))) aliases.unshift(name);
  const detector_classes = [...new Set(draft.detector_classes.map((c) => c.trim()).filter(Boolean))];
  // the extra rules only mean something for a detectable ingredient
  return {
    name,
    aliases,
    detector_classes,
    extra_min_count: detector_classes.length ? draft.extra_min_count : null,
    extra_min_share: detector_classes.length ? draft.extra_min_share : null,
  };
}

/** ISO stamp without a zone is UTC */
export const parseStamp = (value: string | null | undefined): number | null => {
  if (!value) return null;
  const ts = Date.parse(/[zZ]|[+-]\d\d:?\d\d$/.test(value) ? value : `${value}Z`);
  return Number.isFinite(ts) ? ts : null;
};
